import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { SubscriptionAccount, Subscription, Filter } from '../store/store'
import { RequestState } from '../../types'
import { useHistory } from 'react-router-dom'
import './SubscriptionAccountsCompact.css'
import PropertyGrid from '../../../components/PropertyGrid'
import WarningText from '../../../components/text/WarningText'
import AlertText from '../../../components/text/AlertText'
import Date from '../../../components/Date'
import slugify from 'react-slugify'

type Props = {
  subscription: Subscription
  loadSubscriptionAccountFilters: (subscriptionId: string, accountId: string) => Promise<void>
}

function SubscriptionAccountsCompact(props: Props) {
  const { subscription, loadSubscriptionAccountFilters } = props
  const { t } = useTranslation(['accounts', 'navigation', 'global'])
  const history = useHistory()

  useEffect(() => {
    if (subscription.accounts.length > 0) {
      subscription.accounts.forEach((subscriptionAccount: SubscriptionAccount) => {
        if (subscriptionAccount.requestState === RequestState.NotLoaded && subscriptionAccount.active) {
          loadSubscriptionAccountFilters(subscription.id, subscriptionAccount.id)
        }
      })
    }
  },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subscription.accounts])

  return (
    <div>
      {subscription.requestState === RequestState.Loading && subscription.accounts.length === 0 && (
        <div>
          <i className="fas fa-spinner fa-spin"></i>&nbsp;
          <span>{t('global:loading')}</span>
        </div>
      )}

      {subscription.requestState !== RequestState.Loading && subscription.accounts.length === 0 && (
        <div id="account-message" style={{ marginTop: 10 }}>
          {t('no_accounts')}
        </div>
      )}

      {subscription.accounts.length > 0 && (
        <div id="accounts">
          <div className="account-header">
            <b>{t('navigation:accounts')}</b>
          </div>

          {subscription.accounts.map((account: SubscriptionAccount) => (
            <div key={account.id} className="account" data-cy={slugify(account.name)}>
              <PropertyGrid
                cy-prefix="account"
                properties={{
                  [t('fields.name')]: (
                    <div className="flex-justify-start">
                      <span className={!account.active ? ' inactive' : ''}>{account.name}</span>
                      <div className="action-container">
                        <a
                          href={`${process.env.REACT_APP_WEPAPP_URL}/accounts/${account.id}`}
                          target="_blank"
                          rel="noopener noreferrer">
                          <i className="far fa-window-maximize icon-button web-expand"></i>
                        </a>
                        <span data-cy="btn-account-admin-compact" onClick={() => history.push(`/account/${account.id}/settings`)}>
                          <i className="fas fa-cog icon-button"></i>
                        </span>
                      </div>
                    </div>
                  ),
                  [t('fields.last_activity')]: (
                    <span>
                      {!account.active ? t('deactivated') :
                        account.lastActive
                          ? <Date date={account.lastActive as Date} />
                          : t('no_activity')
                      }
                    </span>
                  ),
                  [t('fields.records')]: (
                    <span className={(!account.active ? ' inactive' : '')}>{account.records}</span>
                  ),
                  [t('filtersAlert')]: (
                    <div>
                      {account.active && account.filters &&
                        account.filters.filter((filter: Filter) => filter.profile === 'alert')
                          .map((filter: Filter) => (
                            <div key={filter.id}>
                              {filter.count > 0 && (
                                <AlertText text={`${filter.count} ${filter.name}`} />
                              )}
                            </div>
                          ))}
                    </div>
                  ),
                  [t('filtersWarn')]: (
                    <div>
                      {account.active && account.filters &&
                        account.filters.filter((filter: Filter) => filter.profile === 'warn')
                          .map((filter: Filter) => (
                            <div key={filter.id}>
                              {filter.count > 0 && (
                                <WarningText text={`${filter.count} ${filter.name}`} />
                              )}
                            </div>
                          ))}
                    </div>
                  )
                }}
              />
            </div>
          ))}
        </div>
      )}
    </div >
  )
}

export default SubscriptionAccountsCompact
